import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from './AddNewUnit.module.css';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';

const AddNewUnit = ({ onSave, onCancel, selectedCategory = 'unit', projectId }) => {
    const dispatch = useDispatch();
    const [unitData, setUnitData] = useState({
        label: '',
        unitID: '',
        unit_type: 'Residential',
        building: '',
        unit_no: '',
        floor: '',
        price: '',
        description: '',
        location: '',
        project_id: projectId
    });
    const [priceDisplay, setPriceDisplay] = useState(''); // For formatted display
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Format number with commas for display
    const formatNumber = (num) => {
        if (!num || num === '') return '';
        const cleanedNum = num.toString().replace(/,/g, '');
        const number = parseFloat(cleanedNum);

        if (isNaN(number)) return '';

        // Handle decimal numbers properly
        const parts = cleanedNum.split('.');
        const integerPart = parseInt(parts[0], 10);
        const decimalPart = parts[1];

        let formattedInteger = integerPart.toLocaleString();

        // If there's a decimal part, add it back
        if (decimalPart !== undefined) {
            return `${formattedInteger}.${decimalPart}`;
        }

        return formattedInteger;
    };

    // Remove formatting to get raw number
    const parseFormattedNumber = (formattedStr) => {
        if (!formattedStr) return '';
        return formattedStr.replace(/,/g, '');
    };

    const handleFieldChange = (field, value) => {
        if (field === 'price') {
            // Remove commas for processing
            const rawValue = parseFormattedNumber(value);

            // Allow empty string
            if (value === '') {
                setUnitData(prev => ({
                    ...prev,
                    [field]: ''
                }));
                setPriceDisplay('');
                return;
            }

            // Validate decimal input (max 2 decimal places)
            const decimalRegex = /^\d+(\.\d{0,2})?$/;
            if (decimalRegex.test(rawValue)) {
                const numValue = parseFloat(rawValue);

                // Allow positive numbers (including decimals)
                if (numValue >= 0 && !isNaN(numValue)) {
                    setUnitData(prev => ({
                        ...prev,
                        [field]: rawValue
                    }));
                    // Update display value with formatting
                    setPriceDisplay(formatNumber(rawValue));
                }
            }
        } else {
            setUnitData(prev => ({
                ...prev,
                [field]: value
            }));
        }
    };

    const handleImageUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.multiple = true;

        fileInput.onchange = (e) => {
            const files = Array.from(e.target.files);
            console.log('Files selected:', files.length);
            files.forEach((file, index) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const newId = Date.now() + index;
                    console.log('Adding image with ID:', newId);
                    setImages(prev => {
                        const newImages = [...prev, {
                            id: newId,
                            src: event.target.result,
                            alt: `Unit Image ${newId}`,
                            file: file
                        }];
                        console.log('Total images after adding:', newImages.length);
                        return newImages;
                    });
                };
                reader.readAsDataURL(file);
            });
        };

        fileInput.click();
    };

    const removeImage = (imageId) => {
        console.log('Removing image with ID:', imageId);
        console.log('Current images before removal:', images);
        setImages(prev => {
            const filtered = prev.filter(img => img.id !== imageId);
            console.log('Images after removal:', filtered);
            return filtered;
        });
    };

    const handleSave = async () => {
        if (unitData.label && unitData.unitID && unitData.building && unitData.unit_no && unitData.floor && unitData.price && unitData.description && unitData.location) {
            setLoading(true);
            setError(null);

            try {
                // Create FormData for file upload
                const formData = new FormData();

                // Add text fields
                formData.append('label', unitData.label);
                formData.append('unitID', unitData.unitID);
                formData.append('unit_type', unitData.unit_type);
                formData.append('building', unitData.building);
                formData.append('unit_no', unitData.unit_no);
                formData.append('floor', unitData.floor);
                formData.append('price', parseFormattedNumber(unitData.price));
                formData.append('description', unitData.description);
                formData.append('location', unitData.location);
                formData.append('project_id', unitData.project_id);

                // Add image files
                images.forEach((image, index) => {
                    if (image.file) {
                        formData.append('images', image.file);
                    }
                });

                await onSave(formData);
            } catch (error) {
                setError(error.message);
                dispatch(showErrorNotification(`Failed to create unit: ${error.message}`));
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.backSection}>
                        <button className={styles.backButton} onClick={onCancel}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <h2>Add New {selectedCategory === 'mall' ? 'Mall' : 'Unit'}</h2>
                    </div>
                </div>

                <div className={styles.mainContent}>
                    <div className={styles.leftContent}>
                        <div className={styles.formSection}>
                            <div className={styles.formGroup}>
                                <label>Label</label>
                                <input
                                    type="text"
                                    value={unitData.label}
                                    onChange={(e) => handleFieldChange('label', e.target.value)}
                                    placeholder="Enter unit label"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Unit ID</label>
                                <input
                                    type="text"
                                    value={unitData.unitID}
                                    onChange={(e) => handleFieldChange('unitID', e.target.value)}
                                    placeholder="Enter unit ID"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Unit Type</label>
                                <select
                                    value={unitData.unit_type}
                                    onChange={(e) => handleFieldChange('unit_type', e.target.value)}
                                    className={styles.input}
                                >
                                    <option value="Residential">Residential</option>
                                    <option value="Chalet">Chalet</option>
                                    <option value="Commercial">Commercial</option>
                                    <option value="Administration">Administration</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Building</label>
                                <input
                                    type="text"
                                    value={unitData.building}
                                    onChange={(e) => handleFieldChange('building', e.target.value)}
                                    placeholder="Enter building"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Unit Number</label>
                                <input
                                    type="text"
                                    value={unitData.unit_no}
                                    onChange={(e) => handleFieldChange('unit_no', e.target.value)}
                                    placeholder="Enter unit number"
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Floor</label>
                                <input
                                    type="text"
                                    value={unitData.floor}
                                    onChange={(e) => handleFieldChange('floor', e.target.value)}
                                    placeholder="Enter floor"
                                    className={styles.input}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Price</label>
                                <input
                                    type="text"
                                    value={priceDisplay}
                                    onChange={(e) => handleFieldChange('price', e.target.value)}
                                    placeholder="Enter price"
                                    className={styles.input}
                                    onKeyDown={(e) => {
                                        // Allow only numbers, backspace, delete, arrow keys, and decimal point
                                        const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
                                            'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                                            'ArrowUp', 'ArrowDown', 'Tab', '.'];
                                        if (!allowedKeys.includes(e.key)) {
                                            e.preventDefault();
                                        }
                                        // Prevent negative sign
                                        if (e.key === '-') {
                                            e.preventDefault();
                                        }
                                        // Prevent multiple decimal points
                                        if (e.key === '.' && e.target.value.includes('.')) {
                                            e.preventDefault();
                                        }
                                        // Prevent more than 2 decimal places
                                        if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(e.key)) {
                                            const currentValue = e.target.value;
                                            const decimalIndex = currentValue.indexOf('.');
                                            if (decimalIndex !== -1) {
                                                const afterDecimal = currentValue.substring(decimalIndex + 1);
                                                const cursorPosition = e.target.selectionStart;
                                                // If cursor is after decimal and we already have 2 digits, prevent input
                                                if (cursorPosition > decimalIndex && afterDecimal.length >= 2) {
                                                    e.preventDefault();
                                                }
                                            }
                                        }
                                    }}
                                />
                                {unitData.price && parseFloat(unitData.price) < 0 && (
                                    <span className={styles.errorText}>Price must be a positive value</span>
                                )}
                            </div>

                            <div className={styles.formGroup}>
                                <label>Description</label>
                                <textarea
                                    value={unitData.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    placeholder="Enter description"
                                    className={styles.textarea}
                                    rows="4"
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Location</label>
                                <input
                                    type="text"
                                    value={unitData.location}
                                    onChange={(e) => handleFieldChange('location', e.target.value)}
                                    placeholder="Enter location"
                                    className={styles.input}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightContent}>
                        <div className={styles.imageSection}>
                            <h3>{selectedCategory === 'mall' ? 'Mall' : 'Unit'} Images</h3>
                            {images.length === 0 ? (
                                <div className={styles.uploadContainer}>
                                    <div className={styles.uploadIcon}>
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7,10 12,15 17,10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    </div>
                                    <p className={styles.uploadText}>Upload {selectedCategory === 'mall' ? 'Mall' : 'Unit'} Images</p>
                                    <button
                                        className={styles.uploadButton}
                                        onClick={handleImageUpload}
                                    >
                                        Choose Files
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className={styles.imageGrid}>
                                        {images.map(image => (
                                            <div key={image.id} className={styles.imageItem}>
                                                <img src={image.src} alt={image.alt} />
                                                <button
                                                    className={styles.removeButton}
                                                    onClick={() => removeImage(image.id)}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        className={styles.uploadButton}
                                        onClick={handleImageUpload}
                                    >
                                        Upload More Images
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className={styles.errorMessage}>
                        <p>{error}</p>
                    </div>
                )}

                <div className={styles.actions}>
                    <button
                        className={styles.cancelButton}
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        className={styles.saveButton}
                        onClick={handleSave}
                        disabled={!unitData.label || !unitData.unitID || !unitData.building || !unitData.unit_no || !unitData.floor || !unitData.price || !unitData.description || !unitData.location || loading}
                    >
                        {loading ? (
                            <>
                                <span className={styles.spinner}></span>
                                Saving...
                            </>
                        ) : (
                            'Save Unit'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddNewUnit; 